import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Card, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/LandingPage.css';
import heroImage from '../assets/svg/finance_hero.svg';
import { FaCheckCircle, FaChartLine, FaShieldAlt, FaUsersCog } from 'react-icons/fa';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { useAuth } from '../utils/AuthContext';

const LandingPage = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!authLoading && currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, authLoading, navigate]);

  const features = [
    {
      title: 'Smart Expense Tracking',
      description: 'Automatically categorize and track your expenses in real-time. Get instant insights into your spending patterns.',
      icon: 'ri-money-dollar-circle-line',
      highlights: ['Auto-categorization', 'Real-time tracking', 'Custom categories']
    },
    {
      title: 'Intelligent Budget Management',
      description: 'Set smart budgets that adapt to your spending habits. Receive timely alerts and recommendations.',
      icon: 'ri-wallet-3-line',
      highlights: ['Flexible budgets', 'Smart alerts', 'AI recommendations']
    },
    {
      title: 'Advanced Financial Reports',
      description: 'Get detailed insights with interactive charts and customizable reports. Make informed financial decisions.',
      icon: 'ri-pie-chart-line',
      highlights: ['Interactive charts', 'Custom reports', 'Export options']
    },
    {
      title: 'Smart Bill Management',
      description: 'Never miss a payment with automated reminders and bill tracking. Schedule and manage all your bills in one place.',
      icon: 'ri-notification-line',
      highlights: ['Payment reminders', 'Bill scheduling', 'Payment history']
    }
  ];

  const benefits = [
    {
      title: 'Save More Money',
      description: 'Users typically save 15-20% more with our smart budgeting tools',
      icon: <FaChartLine className="benefit-icon" />
    },
    {
      title: 'Bank-Level Security',
      description: 'Your data is protected with enterprise-grade encryption',
      icon: <FaShieldAlt className="benefit-icon" />
    },
    {
      title: 'Easy to Use',
      description: 'Intuitive interface designed for users of all experience levels',
      icon: <FaUsersCog className="benefit-icon" />
    }
  ];

  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: [
        'Basic expense tracking',
        'Simple budgeting',
        'Monthly reports',
        'Email support'
      ]
    },
    {
      name: 'Premium',
      price: '$2',
      period: 'per month',
      features: [
        'Advanced expense analytics',
        'AI-powered insights',
        'Custom categories',
        'Priority support',
        'Export data',
        'Bill reminders'
      ],
      recommended: true
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const functions = getFunctions();
      const sendContactEmail = httpsCallable(functions, 'BugOrFeature');

      await sendContactEmail({
        ...formData,
      });

      toast.success('Message sent successfully!');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });


    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <header className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="hero-content">
              <Badge bg="primary" className="mb-3">New: AI-Powered Insights</Badge>
              <h1 className="hero-title">Master Your Finances with Smart Technology</h1>
              <p className="hero-subtitle">
                Transform your financial life with our AI-powered finance management platform. Track expenses, optimize budgets, and achieve your financial goals faster.
              </p>
              <div className="hero-buttons">
                <Link to="/auth/signup" className="btn btn-primary btn-lg me-3">Start Free Trial</Link>
                <a href="#features" className="btn btn-outline-primary btn-lg">See Features</a>
              </div>
              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-number">50K+</span>
                  <span className="stat-label">Active Users</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">$2M+</span>
                  <span className="stat-label">Money Saved</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">4.8/5</span>
                  <span className="stat-label">User Rating</span>
                </div>
              </div>
            </Col>
            <Col lg={6} className="hero-image-container">
              <img src={heroImage} alt="Finance Management" className="hero-image" />
            </Col>
          </Row>
        </Container>
      </header>

      {/* Benefits Section */}
      <section className="benefits-section">
        <Container>
          <Row>
            {benefits.map((benefit, index) => (
              <Col md={4} key={index} className="benefit-item">
                <div className="benefit-icon-wrapper">
                  {benefit.icon}
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <Container>
          <div className="text-center mb-5">
            <Badge bg="primary" className="mb-3">Powerful Features</Badge>
            <h2 className="section-title">Everything You Need to Succeed</h2>
            <p className="section-subtitle">Comprehensive tools to help you manage every aspect of your finances</p>
          </div>
          <Row>
            {features.map((feature, index) => (
              <Col md={6} lg={3} key={index}>
                <Card className="feature-card">
                  <Card.Body>
                    <div className="feature-icon-wrapper">
                      <i className={`${feature.icon} feature-icon`}></i>
                    </div>
                    <Card.Title>{feature.title}</Card.Title>
                    <Card.Text>{feature.description}</Card.Text>
                    <ul className="feature-highlights">
                      {feature.highlights.map((highlight, idx) => (
                        <li key={idx}>
                          <FaCheckCircle className="check-icon" /> {highlight}
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <Container>
          <div className="text-center mb-5">
            <Badge bg="primary" className="mb-3">Simple Pricing</Badge>
            <h2 className="section-title">Choose Your Plan</h2>
            <p className="section-subtitle">Start free and upgrade when you need more features</p>
          </div>
          <Row className="justify-content-center">
            {pricingTiers.map((tier, index) => (
              <Col md={6} lg={4} key={index}>
                <Card className={`pricing-card ${tier.recommended ? 'recommended' : ''}`}>
                  {tier.recommended && <div className="recommended-badge">Most Popular</div>}
                  <Card.Body>
                    <h3 className="pricing-tier">{tier.name}</h3>
                    <div className="pricing-price">
                      <span className="amount">{tier.price}</span>
                      <span className="period">/{tier.period}</span>
                    </div>
                    <ul className="pricing-features">
                      {tier.features.map((feature, idx) => (
                        <li key={idx}>
                          <FaCheckCircle className="check-icon" /> {feature}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={tier.name === 'Free' ? "/auth/signup" : "/auth/signup"}
                      className={`btn btn-${tier.recommended ? 'primary' : 'outline-primary'} btn-block`}
                    >
                      {tier.name === 'Free' ? 'Get Started' : 'Upgrade Now'}
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <Container>
          <Row>
            <Col lg={6}>
              <Badge bg="primary" className="mb-3">Get In Touch</Badge>
              <h2 className="section-title">We're Here to Help</h2>
              <p className="section-subtitle">Have questions about our platform? Our team is ready to assist you.</p>
              <div className="contact-info">
                <div className="contact-item">
                  <i className="ri-mail-line"></i>
                  <div>
                    <h4>Email Us</h4>
                    <p>support@financemanager.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="ri-phone-line"></i>
                  <div>
                    <h4>Call Us</h4>
                    <p>+971 508875048</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="ri-map-pin-line"></i>
                  <div>
                    <h4>Visit Us</h4>
                    <p>Dubai Internet City<br />Building 14<br />Dubai, United Arab Emirates</p>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <Form onSubmit={handleSubmit} className="contact-form">
                <Form.Group className="mb-3">
                  <Form.Control type="text" placeholder="Your Name" required value={formData.name} onChange={handleChange} name='name' />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Control type="email" placeholder="Your Email" required value={formData.email} onChange={handleChange} name='email' />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Select value={formData.subject} onChange={handleChange} name='subject'>
                    <option>Select Topic</option>
                    <option>General Inquiry</option>
                    <option>Technical Support</option>
                    <option>Pricing Questions</option>
                    <option>Feature Request</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Control as="textarea" rows={4} placeholder="Your Message" required value={formData.message} onChange={handleChange} name='message' />
                </Form.Group>
                <Button type="submit" variant="primary" size="lg" className="w-100">Send Message</Button>
              </Form>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="footer">
        <Container>
          <Row className="footer-content">
            <Col md={4}>
              <h4>Finance Manager</h4>
              <p>Smart financial management solution based in Dubai, UAE. Helping individuals and businesses across the Middle East track expenses, manage budgets, and achieve their financial goals.</p>
            </Col>
            <Col md={2}>
              <h5>Product</h5>
              <ul className="footer-links">
                <li><a href="#features">Features</a></li>
                <li><a href="#pricing">Pricing</a></li>
              </ul>
            </Col>
            <Col md={2}>
              <div 
                className="d-flex justify-content-center align-items-center mt-4 mb-4 px-3"
                style={{ width: '100%' }}
              >
                <a 
                  href="https://www.producthunt.com/posts/finance-management?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-finance&#0045;management" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-block text-center"
                >
                  <img 
                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=950308&theme=dark&t=1748545697397" 
                    alt="Finance Management - Personal finance & budget tracker | Product Hunt" 
                    style={{ 
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      margin: '0 auto'
                    }} 
                    width="250" 
                    height="75" 
                  />
                </a>
              </div>
            </Col>
            <Col md={4}>
              <h5>Office</h5>
              <p>Dubai Internet City<br />Building 14<br />Dubai, UAE</p>
              <p>Phone: +971 4 123 4567<br />Email: support@financemanager.ae</p>
            </Col>
          </Row>
          <hr />
          <Row className="footer-bottom">
            <Col md={6}>
              <p>&copy; 2024 Finance Manager. All rights reserved.</p>
            </Col>
            <Col md={6} className="text-md-end">
              <a href="#privacy" className="me-3">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPage; 